import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages the Account panel, mobile overflow
 * controls and cart announcements.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  #accountCloseTimer = 0;
  #escapeFocusTimer = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.addEventListener('click', this.#onHeaderClick);
    this.querySelector('[data-bce-account-panel]')?.addEventListener('cancel', this.#onAccountCancel);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('pointerdown', this.#onPointerDown, true);
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('bce:header-panel-opening', this.#onHeaderPanelOpening);
    window.addEventListener('resize', this.#onViewportChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#accountCloseTimer);
    window.clearTimeout(this.#escapeFocusTimer);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.removeEventListener('click', this.#onHeaderClick);
    this.querySelector('[data-bce-account-panel]')?.removeEventListener('cancel', this.#onAccountCancel);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('pointerdown', this.#onPointerDown, true);
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('bce:header-panel-opening', this.#onHeaderPanelOpening);
    window.removeEventListener('resize', this.#onViewportChange);
  }

  #setMoreMenu = (open) => {
    const toggle = this.querySelector('[data-bce-header-more-toggle]');
    const menu = this.querySelector('[data-bce-header-more-menu]');
    if (!(toggle instanceof HTMLButtonElement) || !(menu instanceof HTMLElement)) return;

    toggle.setAttribute('aria-expanded', String(open));
    menu.classList.toggle('is-open', open);
  };

  #positionAccountPanel = () => {
    const trigger = this.querySelector('[data-bce-account-trigger]');
    const panel = this.querySelector('[data-bce-account-panel]');
    if (!(trigger instanceof HTMLButtonElement) || !(panel instanceof HTMLDialogElement)) return;

    const rect = trigger.getBoundingClientRect();
    panel.style.setProperty('--bce-account-panel-top', `${Math.round(rect.bottom + 12)}px`);
    panel.style.setProperty('--bce-account-panel-right', `${Math.max(12, Math.round(window.innerWidth - rect.right))}px`);
  };

  #setAccountPanel = (open, restoreFocus = false) => {
    const trigger = this.querySelector('[data-bce-account-trigger]');
    const panel = this.querySelector('[data-bce-account-panel]');
    if (!(trigger instanceof HTMLButtonElement) || !(panel instanceof HTMLDialogElement)) return;

    window.clearTimeout(this.#accountCloseTimer);
    trigger.setAttribute('aria-expanded', String(open));

    if (open) {
      document.dispatchEvent(new CustomEvent('bce:header-panel-opening', { detail: { source: 'account' } }));
      this.#positionAccountPanel();
      if (!panel.open) panel.showModal();
      window.requestAnimationFrame(() => panel.classList.add('is-open'));
      return;
    }

    panel.classList.remove('is-open');
    this.#accountCloseTimer = window.setTimeout(() => {
      if (panel.open && trigger.getAttribute('aria-expanded') === 'false') panel.close();
    }, 180);

    if (restoreFocus) trigger.focus({ preventScroll: true });
  };

  #onAccountCancel = (event) => {
    event.preventDefault();
    this.#setAccountPanel(false, true);
  };

  #onViewportChange = () => {
    const panel = this.querySelector('[data-bce-account-panel]');
    if (panel instanceof HTMLDialogElement && panel.open) this.#positionAccountPanel();
  };

  #onHeaderClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const accountTrigger = target.closest('[data-bce-account-trigger]');
    if (accountTrigger) {
      event.preventDefault();
      const open = accountTrigger.getAttribute('aria-expanded') !== 'true';
      this.#setMoreMenu(false);
      this.#setAccountPanel(open);
      return;
    }

    if (target.closest('[data-bce-account-close]')) {
      event.preventDefault();
      this.#setAccountPanel(false, true);
      return;
    }

    const accountPanel = this.querySelector('[data-bce-account-panel]');
    if (target === accountPanel) {
      this.#setAccountPanel(false, true);
      return;
    }

    if (target.closest('[data-bce-account-panel] a')) {
      this.#setAccountPanel(false);
      return;
    }

    const moreToggle = target.closest('[data-bce-header-more-toggle]');
    if (moreToggle) {
      event.preventDefault();
      const open = moreToggle.getAttribute('aria-expanded') !== 'true';
      this.#setAccountPanel(false);
      this.#setMoreMenu(open);
      return;
    }

    const cartTrigger = target.closest('[data-testid="cart-drawer-trigger"]');

    if (cartTrigger) {
      const cartDialog = cartTrigger.closest('cart-drawer-component')?.querySelector('dialog');

      if (cartDialog instanceof HTMLDialogElement) {
        const closeMoreMenu = () => this.#setMoreMenu(false);
        cartDialog.addEventListener('close', closeMoreMenu, { once: true });

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (!cartDialog.open) {
              cartDialog.removeEventListener('close', closeMoreMenu);
              this.#setMoreMenu(false);
            }
          });
        });
      }

      document.dispatchEvent(new CustomEvent('bce:header-panel-opening', { detail: { source: 'cart' } }));
      this.#setAccountPanel(false);
      return;
    }

    if (target.closest('[data-bce-liked-drawer-trigger]')) {
      document.dispatchEvent(new CustomEvent('bce:header-panel-opening', { detail: { source: 'liked' } }));
      this.#setAccountPanel(false);
    }

    if (target.closest('[data-bce-header-more-menu]') && target.closest('a, button')) {
      window.requestAnimationFrame(() => this.#setMoreMenu(false));
    }
  };

  #onHeaderPanelOpening = (event) => {
    const source = event.detail?.source;
    if (source !== 'account') this.#setAccountPanel(false);
    if (source !== 'more' && source !== 'cart') this.#setMoreMenu(false);
  };

  #onDocumentClick = (event) => {
    if (!(event.target instanceof Node) || this.contains(event.target)) return;
    this.#setMoreMenu(false);
    this.#setAccountPanel(false);
  };

  #clearEscapeFocus = () => {
    window.clearTimeout(this.#escapeFocusTimer);
    document.querySelectorAll('.bce-escape-focus').forEach((element) => {
      element.classList.remove('bce-escape-focus');
    });
  };

  #markEscapeFocus = () => {
    const activeElement = document.activeElement;
    if (
      activeElement instanceof HTMLElement &&
      activeElement.matches(
        '#header-component .menu-list__link--bce-desktop-dropdown, #header-component .bce-account-trigger, #header-component [data-bce-liked-drawer-trigger], #header-component [data-testid="cart-drawer-trigger"], #header-component .header__icon--menu'
      )
    ) {
      activeElement.classList.add('bce-escape-focus');
    }
  };

  #onPointerDown = () => {
    this.#clearEscapeFocus();
  };

  #onKeyDown = (event) => {
    if (event.key === 'Tab') {
      this.#clearEscapeFocus();
      return;
    }

    if (event.key !== 'Escape') return;
    this.#setMoreMenu(false);
    window.requestAnimationFrame(this.#markEscapeFocus);
    window.clearTimeout(this.#escapeFocusTimer);
    this.#escapeFocusTimer = window.setTimeout(this.#markEscapeFocus, 240);
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
